import { type MoyenPaiement, paiements as sch } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, corps, ecrit, lire, requeteMoi, requetes } from '../api'
import { LIBELLES_MOYEN } from '../libelles'
import { type Champ, Formulaire } from './Formulaire'
import { Section } from './Section'

// Paiement attendu saisi à la main depuis une pièce (facture, avis) qui n'en a pas : prérempli
// d'après la lecture, lié à la pièce. Un virement ou un chèque à émettre crée sa tâche « Payer ».

const MOYEN_DU_REGLEMENT: Partial<Record<string, MoyenPaiement>> = {
  virement: 'virement_emis',
  prelevement: 'prelevement',
  carte: 'carte',
  cheque: 'cheque',
}

export function PaiementDepuisPiece({ pieceId }: { pieceId: string }) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const piece = useQuery(requetes.piece(pieceId))
  const tiers = useQuery(requetes.tiers)
  const p = piece.data
  if (!p || !moi || !p.entiteId || p.statut !== 'rattachee') return null
  if (p.paiements.length > 0 || (p.type !== 'facture' && p.type !== 'avis')) return null
  if (!ecrit(moi, p.entiteId)) return null
  const entiteId = p.entiteId
  const x = p.extraction
  const sens = x?.sens === 'emise' ? 'encaisser' : 'payer'

  const champs: Champ[] = [
    {
      nom: 'libelle',
      libelle: 'Libellé',
      type: 'texte',
      aide: 'Ses mots aident le rapprochement avec le relevé.',
    },
    {
      nom: 'tiersId',
      libelle: 'Tiers',
      type: 'choix',
      options: (tiers.data ?? []).map((t) => ({ valeur: t.id, libelle: t.nom })),
    },
    { nom: 'montantDuCts', libelle: 'Montant (€)', type: 'montant', requis: true },
    {
      nom: 'datePrevue',
      libelle: sens === 'payer' ? 'À payer avant le' : 'Attendu le',
      type: 'date',
    },
    {
      nom: 'moyen',
      libelle: 'Moyen',
      type: 'choix',
      options: Object.entries(LIBELLES_MOYEN).map(([valeur, libelle]) => ({ valeur, libelle })),
      aide: 'Virement ou chèque à émettre : une tâche « Payer » est créée.',
    },
  ]
  const initial = {
    libelle:
      [p.emetteur ?? x?.emetteur.nom, p.numero ?? x?.numero].filter(Boolean).join(' ') || null,
    tiersId: p.emetteurId,
    montantDuCts: x?.montants.aPayerCts ?? x?.montants.ttcCts ?? null,
    datePrevue: x?.dateEcheance ?? null,
    moyen:
      (x && MOYEN_DU_REGLEMENT[x.modeReglement]) ?? (sens === 'payer' ? 'virement_emis' : null),
  }

  return (
    <Section
      titre="Paiement"
      libelleAjout="Créer le paiement attendu"
      ajout={(fermer) => (
        <Formulaire
          champs={champs}
          initial={initial}
          libelleBouton="Créer le paiement"
          onAnnuler={fermer}
          onValider={async (v) => {
            await lire(
              api.paiements.$post({
                json: corps(sch.paiementCreation, {
                  ...v,
                  sens,
                  entiteId,
                  pieceId,
                  devise: p.devise ?? 'EUR',
                }),
              }),
            )
            await client.invalidateQueries()
            fermer()
          }}
        />
      )}
    >
      <p className="discret">
        {sens === 'payer'
          ? 'Aucun paiement attendu : l’appli ne rappellera pas de payer cette pièce.'
          : 'Aucun encaissement attendu pour cette pièce.'}
      </p>
    </Section>
  )
}
